// tabs/ThinkyBaaSSmartContracts.jsx
import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlay, faCheckCircle, faFileContract, faUpload } from "@fortawesome/free-solid-svg-icons";

// Dummy contracts with safe defaults
const dummyContracts = [
  {
    id: 1,
    name: "Invoice Payment Contract",
    description:
      "Automatically releases payment to vendor once goods are delivered and verified.",
    deployed: false,
    executed: false,
    verified: false,
    log: [],
    precondition: true,
  },
  {
    id: 2,
    name: "Supply Chain Tracking",
    description:
      "Logs shipment milestones and triggers notifications for stakeholders.",
    deployed: false,
    executed: false,
    verified: false,
    log: [],
    precondition: false,
  },
  {
    id: 3,
    name: "Education Certificate Issuance",
    description:
      "Automatically issues blockchain-verified certificates upon course completion.",
    deployed: false,
    executed: false,
    verified: false,
    log: [],
    precondition: true,
  },
];

const ThinkyBaaSSmartContracts = () => {
  const [contracts, setContracts] = useState(
    dummyContracts.map((c) => ({ ...c, log: c.log || [] }))
  );

  // Deploy contract
  const deployContract = (id) => {
    setContracts((prev) =>
      prev.map((c) =>
        c.id === id && !c.deployed
          ? {
              ...c,
              deployed: true,
              log: [...(c.log || []), { event: "Deployed", time: new Date().toLocaleTimeString() }],
            }
          : c
      )
    );
  };

  // Execute contract
  const executeContract = (id) => {
    setContracts((prev) =>
      prev.map((c) => {
        if (c.id === id) {
          if (!c.precondition) {
            return {
              ...c,
              log: [...(c.log || []), { event: "Execution blocked: precondition not met", time: new Date().toLocaleTimeString() }],
            };
          }
          return {
            ...c,
            executed: true,
            log: [...(c.log || []), { event: "Executed", time: new Date().toLocaleTimeString() }],
          };
        }
        return c;
      })
    );
  };

  // Verify contract
  const verifyContract = (id) => {
    setContracts((prev) =>
      prev.map((c) =>
        c.id === id && c.executed && !c.verified
          ? {
              ...c,
              verified: true,
              log: [...(c.log || []), { event: "Verified on ledger", time: new Date().toLocaleTimeString() }],
            }
          : c
      )
    );
  };

  return (
    <div className="bg-gray-900 p-6 rounded-2xl shadow-lg space-y-6">
      <div className="flex items-center mb-4">
        <FontAwesomeIcon icon={faFileContract} className="text-blue-400 text-3xl mr-4" />
        <h2 className="text-lg lg:text-xl font-bold">Smart Contracts Full Workflow</h2>
      </div>

      <p className="text-gray-300">
        This page simulates a blockchain <span className="font-semibold">smart contract workflow</span>:
        Deploy → Execute → Verify. Interactions are reflected in the event log below.
      </p>

      <div className="space-y-4">
        {contracts.map((c) => (
          <div key={c.id} className="bg-gray-800 p-4 rounded-xl border border-gray-700">
            <h3 className="text-xl font-semibold mb-1">{c.name}</h3>
            <p className="text-gray-400 mb-2">{c.description}</p>

            <div className="flex flex-wrap gap-2 mb-2">
              <span className={`px-2 py-1 rounded-sm text-sm ${c.deployed ? "bg-green-600" : "bg-yellow-600"}`}>
                {c.deployed ? "Deployed ✅" : "Pending Deployment ⏳"}
              </span>
              <span className={`px-2 py-1 rounded-sn text-sm ${c.executed ? "bg-green-600" : "bg-yellow-600"}`}>
                {c.executed ? "Executed ✅" : "Pending Execution ⏳"}
              </span>
              <span className={`px-2 py-1 rounded-sm text-sm ${c.verified ? "bg-green-600" : "bg-yellow-600"}`}>
                {c.verified ? "Verified ✅" : "Pending Verification ⏳"}
              </span>
            </div>

            <div className="flex flex-wrap gap-2">
              <button
                disabled={c.deployed}
                onClick={() => deployContract(c.id)}
                className={`w-32 text-sm px-3 py-1 rounded-lg font-semibold transition ${
                  c.deployed ? "bg-gray-600 cursor-not-allowed" : "bg-blue-500 hover:bg-blue-600"
                }`}
              >
                <FontAwesomeIcon icon={faUpload} className="mr-1" /> Deploy
              </button>
              <button
                disabled={!c.deployed || c.executed}
                onClick={() => executeContract(c.id)}
                className={`w-32 text-sm px-3 py-1 rounded-lg font-semibold transition ${
                  !c.deployed || c.executed ? "bg-gray-600 cursor-not-allowed" : "bg-blue-500 hover:bg-blue-600"
                }`}
              >
                <FontAwesomeIcon icon={faPlay} className="mr-1" /> Execute
              </button>
              <button
                disabled={!c.executed || c.verified}
                onClick={() => verifyContract(c.id)}
                className={`w-32 text-sm px-3 py-1 rounded-lg font-semibold transition ${
                  !c.executed || c.verified ? "bg-gray-600 cursor-not-allowed" : "bg-blue-500 hover:bg-blue-600"
                }`}
              >
                <FontAwesomeIcon icon={faCheckCircle} className="mr-1" /> Verify
              </button>
            </div>

            <div className="mt-3 bg-gray-900 p-2 rounded-lg border border-gray-700">
              <h4 className="text-gray-300 font-semibold mb-1">Event Log:</h4>
              {(!c.log || c.log.length === 0) ? (
                <p className="text-gray-500 text-sm">No events yet.</p>
              ) : (
                <ul className="text-gray-400 text-sm list-disc list-inside space-y-1">
                  {c.log?.map((log, idx) => (
                    <li key={idx}>
                      [{log.time}] {log.event}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ThinkyBaaSSmartContracts;
